import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "lumen.supportViewAs";
const EVENT_NAME = "lumen:support-view-as";
const LOCATION_EVENT_NAME = "lumen:location-change";

const emptyState = { active: false, target: null };

const normalizeTarget = (target) => {
  if (!target || typeof target !== "object") return null;
  const userId = target.userId == null ? "" : String(target.userId).trim();
  const email = target.email == null ? "" : String(target.email).trim();
  if (!userId && !email) return null;
  return {
    userId: userId || null,
    email: email || null,
    label: target.label == null ? "" : String(target.label).trim(),
    issueNumber: target.issueNumber == null ? "" : String(target.issueNumber).trim(),
    source: target.source == null ? "gitea-issue" : String(target.source).trim(),
    pageUrl: target.pageUrl == null ? "" : String(target.pageUrl).trim(),
    startedAt: target.startedAt || new Date().toISOString(),
  };
};

const readState = () => {
  try {
    if (typeof window === "undefined") return emptyState;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState;
    const parsed = JSON.parse(raw);
    const target = normalizeTarget(parsed?.target || parsed);
    return target ? { active: true, target } : emptyState;
  } catch {
    return emptyState;
  }
};

const writeState = (target) => {
  try {
    if (typeof window === "undefined") return;
    if (!target) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ target }));
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: readState() }));
  } catch {
    // ignore storage failures; current tab state still updates through React.
  }
};

export const getActiveSupportViewAs = () => readState();

export const SupportViewAsContext = createContext({
  active: false,
  target: null,
  routePath: "",
  start: () => false,
  stop: () => {},
});

const readRoutePath = () => {
  try {
    if (typeof window === "undefined") return "";
    return window.location?.pathname || "";
  } catch {
    return "";
  }
};

export function SupportViewAsProvider({ children }) {
  const [state, setState] = useState(readState);
  const [routePath, setRoutePath] = useState(readRoutePath);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onChange = () => setState(readState());
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onLocationChange = () => setRoutePath(readRoutePath());
    const history = window.history;
    const originalPushState = history?.pushState;
    const originalReplaceState = history?.replaceState;

    if (originalPushState && !history.__lumenSupportViewAsPatched) {
      history.pushState = function pushState(...args) {
        const result = originalPushState.apply(this, args);
        window.dispatchEvent(new Event(LOCATION_EVENT_NAME));
        return result;
      };
      history.replaceState = function replaceState(...args) {
        const result = originalReplaceState.apply(this, args);
        window.dispatchEvent(new Event(LOCATION_EVENT_NAME));
        return result;
      };
      history.__lumenSupportViewAsPatched = true;
    }

    window.addEventListener(LOCATION_EVENT_NAME, onLocationChange);
    window.addEventListener("popstate", onLocationChange);
    return () => {
      window.removeEventListener(LOCATION_EVENT_NAME, onLocationChange);
      window.removeEventListener("popstate", onLocationChange);
    };
  }, []);

  const start = useCallback((target) => {
    const normalized = normalizeTarget(target);
    if (!normalized) return false;
    writeState(normalized);
    setState({ active: true, target: normalized });
    return true;
  }, []);

  const stop = useCallback(() => {
    writeState(null);
    setState(emptyState);
  }, []);

  const value = useMemo(
    () => ({
      active: Boolean(state.active && state.target),
      target: state.target || null,
      routePath,
      start,
      stop,
    }),
    [state, routePath, start, stop]
  );

  return (
    <SupportViewAsContext.Provider value={value}>
      {children}
    </SupportViewAsContext.Provider>
  );
}

export const useSupportViewAs = () => useContext(SupportViewAsContext);
